import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Item } from "features/preauthorization/components/NestedPayerResponse";
import { Card, Col, Form, Row, Table } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { motion } from "framer-motion";
import {
  addValidationError,
  clearValidationErrors,
  handleFillStepData,
} from "../config/claimSlice";
import { Eye, PencilSquare } from "react-bootstrap-icons";
import moment from "moment";
import OpticalDetailsEditModal from "./OpticalDetailsEditModal";

// Helper: Generate number range
const generateOptions = (start, end, step, decimals = 2) => {
  const options = [];
  let current = start;
  while (current <= end) {
    options.push(current.toFixed(decimals));
    current += step;
  }
  return options;
};

const OpticalDetailsRequest = () => {
  const dispatch = useDispatch();
  const { stepFormData, viewClaimInfo } = useSelector((state) => state.claim);
  const { allPermissionType } = useSelector((state) => state.role);
  const claimsSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) =>
      item?.rolemngmttyp_name === "Claim" ||
      item?.rolemngmttyp_name === "Claims"
  );
  const [showModal, setShowModal] = useState(false);
  const [initializedFromPrescription, setInitializedFromPrescription] =
    useState(false);
  const payload = viewClaimInfo?.payload_json;
  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      productType: stepFormData?.productType || "",
      optical_date: stepFormData?.optical_date || null,
      regularLenses: stepFormData?.regularLenses || "",
      regular_contact: stepFormData?.regular_contact || "",
      is_frame: stepFormData?.is_frame ?? false,
      lens_specifications: stepFormData?.lens_specifications || [],
    },
  });

  const productType = watch("productType");
  const isLens = productType === "lens";
  const isContact = productType === "contact";

  const status = viewClaimInfo?.claim_status?.toLowerCase();
  const isDisabled = [
    "error",
    "ready to submit",
    "rejected",
    "cancelled",
    "in process",
    "validation error",
    "invalid",
  ].includes(status?.toLowerCase());

  // Memoize options generation
  const getOptions = useCallback((fieldName) => {
    const ranges = {
      sph: [-20.0, 20.0, 0.25],
      power: [-12.0, 12.0, 0.25],
      cyl: [-12.0, 12.0, 0.25],
      add: [-12.0, 12.0, 0.25],
      axis: [1, 180, 1, 0],
      in: [0.0, 10.0, 0.5],
      out: [0.0, 10.0, 0.5],
      down: [0.0, 10.0, 0.5],
      up: [0.0, 10.0, 0.5],
      backcurve: [4.0, 8.0, 0.1, 1],
      diameter: [5.0, 30.0, 0.1, 1],
    };

    return ranges[fieldName] ? generateOptions(...ranges[fieldName]) : [];
  }, []);

  const renderSelect = useCallback(
    (name) => {
      const [_, fieldName] = name.split("_");
      const options = getOptions(fieldName);

      return (
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Form.Select {...field} className="fs-12" size="sm">
              <option value="">Select</option>
              {options.map((val) => (
                <option key={val} value={val}>
                  {val}
                </option>
              ))}
            </Form.Select>
          )}
        />
      );
    },
    [control, getOptions]
  );

  const renderTextInput = useCallback(
    (name) => (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Form.Control
            {...field}
            type="text"
            className="fs-12"
            size="sm"
            placeholder=""
          />
        )}
      />
    ),
    [control]
  );

  const renderDuration = useCallback(
    (name) => (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Form.Control
            {...field}
            type="number"
            className="fs-12"
            size="sm"
            placeholder=""
          />
        )}
      />
    ),
    [control]
  );

  const renderDurationUnit = useCallback(
    (name) => (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Form.Select {...field} className="fs-12" size="sm">
            <option value="">Select</option>
            <option value="D">Days</option>
            <option value="WK">Weeks</option>
            <option value="MO">Months</option>
          </Form.Select>
        )}
      />
    ),
    [control]
  );

  console.log({ stepFormData });

  // Initialize form values from stepFormData
  useEffect(() => {
    if (!stepFormData) return;

    // Set top-level fields
    const fields = [
      "productType",
      "optical_date",
      "regularLenses",
      "regular_contact",
      "is_frame",
      "lens_specifications",
    ];

    fields.forEach((field) => {
      if (stepFormData[field] !== undefined) {
        setValue(field, stepFormData[field]);
      }
    });

    // Set eye-specific fields
    const eyeFields = ["right", "left"];
    const noteTypes = ["distance", "near"];
    const isStepDataContact = stepFormData.productType === "contact";

    eyeFields.forEach((side) => {
      noteTypes.forEach((noteType) => {
        const fieldPrefix = `${side}_`;
        const fieldsToSet = isStepDataContact
          ? ["power", "cyl", "axis", "backcurve", "diameter", "brand", "color"]
          : ["sph", "cyl", "axis", "in", "out", "up", "down", "brand", "color"];

        fieldsToSet.forEach((field) => {
          const fieldName =
            field === "brand" || field === "color"
              ? `${side}_${field}`
              : `${side}_${field}_${noteType}`;

          if (stepFormData[fieldName] !== undefined) {
            setValue(fieldName, stepFormData[fieldName]);
          }
        });

        // Duration fields
        if (isStepDataContact) {
          ["duration", "duration_unit"].forEach((field) => {
            const fieldName = `${side}_${field}`;
            if (stepFormData[fieldName] !== undefined) {
              setValue(fieldName, stepFormData[fieldName]);
            }
          });
        }

        // Add field (not dependent on noteType)
        const addField = `${side}_add`;
        if (stepFormData[addField] !== undefined) {
          setValue(addField, stepFormData[addField]);
        }
      });
    });
  }, [stepFormData, setValue]);

  // Initialize from vision prescription data (only top-level fields from prescp)
  useEffect(() => {
    if (!payload?.prescp || initializedFromPrescription) return;

    const {
      lens_typ,
      prescp_dte,
      isframe,
      regular_lenses,
      lens_specifications,
      regular_contact,
    } = payload.prescp;

    // Set top-level fields only (not from individual entries)
    const fields = {
      productType: lens_typ || "",
      optical_date: prescp_dte ? moment(prescp_dte).toDate() : null,
      is_frame: isframe ?? false,
      regularLenses: regular_lenses || "",
      regular_contact: regular_contact || "",
      lens_specifications: Array.isArray(lens_specifications)
        ? lens_specifications.map((spec) => ({ label: spec, value: spec }))
        : [],
    };

    Object.entries(fields).forEach(([field, value]) => {
      setValue(field, value);
      dispatch(handleFillStepData({ check: field, value }));
    });

    // Initialize all eye-specific data from prescp entries
    Object.values(payload.prescp).forEach((entry) => {
      if (typeof entry !== "object" || !entry?.eye) return;

      const {
        eye,
        note,
        sphere,
        cylinder,
        axis,
        add,
        brand,
        color,
        power,
        bckcurve,
        dmeter,
        duration,
        duration_typ,
        prism,
      } = entry;
      const side = eye.toLowerCase();
      const noteType = note?.[0]?.text?.toLowerCase();

      if (!side || !noteType) return;

      // Determine if it's lens or contact based on productType
      const isLensType = lens_typ === "lens";
      const isContactType = lens_typ === "contact";

      // Initialize sphere (for lens) or power (for contact)
      if (isLensType && sphere !== undefined && sphere !== null) {
        const fieldName = `${side}_sph_${noteType}`;
        const formattedValue = parseFloat(sphere).toFixed(2);
        setValue(fieldName, formattedValue);
        dispatch(
          handleFillStepData({ check: fieldName, value: formattedValue })
        );
      }

      if (isContactType && power !== undefined && power !== null) {
        const fieldName = `${side}_power_${noteType}`;
        const formattedValue = parseFloat(power).toFixed(2);
        setValue(fieldName, formattedValue);
        dispatch(
          handleFillStepData({ check: fieldName, value: formattedValue })
        );
      }

      // Initialize cylinder
      if (cylinder !== undefined && cylinder !== null) {
        const fieldName = `${side}_cyl_${noteType}`;
        const formattedValue = parseFloat(cylinder).toFixed(2);
        setValue(fieldName, formattedValue);
        dispatch(
          handleFillStepData({ check: fieldName, value: formattedValue })
        );
      }

      // Initialize axis
      if (axis !== undefined && axis !== null && axis !== "") {
        const fieldName = `${side}_axis_${noteType}`;
        setValue(fieldName, String(axis));
        dispatch(handleFillStepData({ check: fieldName, value: String(axis) }));
      }

      // Initialize add (not dependent on noteType, but we'll set it for each noteType)
      if (add !== undefined && add !== null) {
        const fieldName = `${side}_add`;
        const formattedValue = parseFloat(add).toFixed(2);
        setValue(fieldName, formattedValue);
        dispatch(
          handleFillStepData({ check: fieldName, value: formattedValue })
        );
      }

      // Initialize brand (not dependent on noteType)
      if (brand !== undefined && brand !== null && brand !== "") {
        const fieldName = `${side}_brand`;
        setValue(fieldName, String(brand));
        dispatch(
          handleFillStepData({ check: fieldName, value: String(brand) })
        );
      }

      // Initialize color (not dependent on noteType)
      if (color !== undefined && color !== null && color !== "") {
        const fieldName = `${side}_color`;
        setValue(fieldName, String(color));
        dispatch(
          handleFillStepData({ check: fieldName, value: String(color) })
        );
      }

      // Initialize backcurve (for contact)
      if (isContactType && bckcurve !== undefined && bckcurve !== null) {
        const fieldName = `${side}_backcurve_${noteType}`;
        const formattedValue = parseFloat(bckcurve).toFixed(1);
        setValue(fieldName, formattedValue);
        dispatch(
          handleFillStepData({ check: fieldName, value: formattedValue })
        );
      }

      // Initialize diameter (for contact)
      if (isContactType && dmeter !== undefined && dmeter !== null) {
        const fieldName = `${side}_diameter_${noteType}`;
        const formattedValue = parseFloat(dmeter).toFixed(1);
        setValue(fieldName, formattedValue);
        dispatch(
          handleFillStepData({ check: fieldName, value: formattedValue })
        );
      }
      if (isContactType && duration !== undefined && duration !== null) {
        const fieldName = `${side}_duration`;
        const formattedValue = parseFloat(duration);
        setValue(fieldName, formattedValue);
        dispatch(
          handleFillStepData({ check: fieldName, value: formattedValue })
        );
      }
      if (
        isContactType &&
        duration_typ !== undefined &&
        duration_typ !== null
      ) {
        const fieldName = `${side}_duration_unit`;
        const formattedValue = duration_typ;
        setValue(fieldName, formattedValue);
        dispatch(
          handleFillStepData({ check: fieldName, value: formattedValue })
        );
      }

      // Initialize prism data
      if (Array.isArray(prism) && prism.length > 0) {
        prism.forEach((prismItem) => {
          const { base, amount } = prismItem;
          if (!base || amount === undefined || amount === null) return;

          const fieldName = `${side}_${base}_${noteType}`;
          const formattedAmount = parseFloat(amount).toFixed(2);

          setValue(fieldName, formattedAmount);
          dispatch(
            handleFillStepData({ check: fieldName, value: formattedAmount })
          );
        });
      }
    });

    setInitializedFromPrescription(true);
  }, [payload, setValue, dispatch, initializedFromPrescription]);

  // Save form data to Redux on submit
  const onSubmit = useCallback(
    (data) => {
      dispatch(clearValidationErrors());
      let shouldBlock = false;

      // Validation helper
      const validateFieldPairs = (cylField, axisField) => {
        const cylValue = data[cylField];
        const axisValue = data[axisField];

        if ((cylValue && !axisValue) || (!cylValue && axisValue)) {
          dispatch(
            addValidationError({
              field: cylValue ? axisField : cylField,
              message: `Both fields must be provided together`,
            })
          );
          return false;
        }
        return true;
      };

      // Lens validation
      if (isLens) {
        // Validate at least one sphere exists
        const hasSphere = [
          "left_sph_distance",
          "left_sph_near",
          "right_sph_distance",
          "right_sph_near",
        ].some((field) => data[field]);

        if (!hasSphere) {
          dispatch(
            addValidationError({
              field: "sph",
              message: "Sphere is required for at least one eye/distance",
            })
          );
          shouldBlock = true;
        }

        // Validate cylinder/axis pairs
        const cylinderAxisPairs = [
          ["left_cyl_distance", "left_axis_distance"],
          ["left_cyl_near", "left_axis_near"],
          ["right_cyl_distance", "right_axis_distance"],
          ["right_cyl_near", "right_axis_near"],
        ];

        cylinderAxisPairs.forEach(([cyl, axis]) => {
          if (!validateFieldPairs(cyl, axis)) {
            shouldBlock = true;
          }
        });

        // Validate directional prisms
        const directions = ["in", "out", "up", "down"];
        directions.forEach((direction) => {
          ["left", "right"].forEach((side) => {
            ["distance", "near"].forEach((noteType) => {
              const dirField = `${side}_${direction}_${noteType}`;
              const sphField = `${side}_sph_${noteType}`;
              if (data[dirField] && !data[sphField]) {
                dispatch(
                  addValidationError({
                    field: sphField,
                    message:
                      "Sphere is required when directional prism is specified",
                  })
                );
                shouldBlock = true;
              }
            });
          });
        });
      }

      // Contact lens validation
      if (isContact) {
        // Validate at least one power exists
        const hasPower = [
          "left_power_distance",
          "left_power_near",
          "right_power_distance",
          "right_power_near",
        ].some((field) => data[field]);

        if (!hasPower) {
          dispatch(
            addValidationError({
              field: "power",
              message: "Power is required for at least one eye/distance",
            })
          );
          shouldBlock = true;
        }

        // Validate cylinder/axis pairs
        const cylinderAxisPairs = [
          ["left_cyl_distance", "left_axis_distance"],
          ["left_cyl_near", "left_axis_near"],
          ["right_cyl_distance", "right_axis_distance"],
          ["right_cyl_near", "right_axis_near"],
        ];

        cylinderAxisPairs.forEach(([cyl, axis]) => {
          if (!validateFieldPairs(cyl, axis)) {
            shouldBlock = true;
          }
        });

        // Validate backcurve/diameter dependencies
        ["backcurve", "diameter"].forEach((field) => {
          ["left", "right"].forEach((side) => {
            ["distance", "near"].forEach((noteType) => {
              const fieldName = `${side}_${field}_${noteType}`;
              const powerField = `${side}_power_${noteType}`;
              if (data[fieldName] && !data[powerField]) {
                dispatch(
                  addValidationError({
                    field: powerField,
                    message: `Power is required when ${field} is specified`,
                  })
                );
                shouldBlock = true;
              }
            });
          });
        });
      }

      if (shouldBlock) return;

      // Save all form fields to Redux
      Object.entries(data).forEach(([name, value]) => {
        dispatch(handleFillStepData({ check: name, value }));
      });

      setShowModal(false);
    },
    [dispatch, isLens, isContact]
  );

  // Memoize optical data
  const opticalData = useMemo(
    () => [
      {
        id: 0,
        title: "Optical Type",
        value: stepFormData?.productType || "",
      },
      {
        id: 1,
        title: "Optical Date",
        value: stepFormData?.optical_date
          ? moment(stepFormData.optical_date).format("DD-MM-YYYY")
          : "",
      },
      {
        id: 2,
        title: "Is Frame",
        value: stepFormData?.is_frame ? "Yes" : "No",
      },
      {
        id: 3,
        title: "Regular Contact",
        value: stepFormData?.regular_contact || "",
      },
      {
        id: 4,
        title: "Regular Lenses",
        value: stepFormData?.regularLenses || "",
      },
      {
        id: 5,
        title: "Lens Specifications",
        value:
          stepFormData?.lens_specifications
            ?.map((item) => item?.label)
            ?.join(" , ") || "",
      },
    ],
    [stepFormData, watch]
  );

  // Memoize eye table rendering
  const renderEyeTable = useCallback(
    (eyeLabel, isEditable = false) => {
      const side = eyeLabel.toLowerCase();
      const fieldPrefix = `${side}_`;

      const renderField = (fieldName, isSelect = true) => {
        if (isEditable) {
          return isSelect
            ? renderSelect(fieldName)
            : renderTextInput(fieldName);
        }
        return stepFormData?.[fieldName] || "";
      };

      return (
        <Table
          bordered
          className="align-middle fs-12"
          style={{ tableLayout: "fixed", border: "1px solid #dee2e6" }}
        >
          <thead>
            <tr>
              <th
                colSpan={10}
                className="text-success border border-secondary fs-12 text-center fs-10 fw-semibold"
              >
                {eyeLabel} Eye
              </th>
            </tr>
            <tr>
              <th
                className="border border-secondary fs-12"
                style={{ width: "15%", minWidth: "80px" }}
              >
                Type
              </th>
              <th
                className="border border-secondary fs-12"
                style={{ width: "12%", minWidth: "60px" }}
              >
                {isLens ? "Sphere" : "Power"}
              </th>
              <th
                className="border border-secondary fs-12"
                style={{ width: "12%", minWidth: "60px" }}
              >
                Cylinder
              </th>
              <th
                className="border border-secondary fs-12"
                style={{ width: "5%", minWidth: "20px" }}
              >
                Axis
              </th>

              {isLens ? (
                <>
                  <th
                    className="border border-secondary fs-12"
                    style={{ width: "8%", minWidth: "60px" }}
                  >
                    In
                  </th>
                  <th
                    className="border border-secondary fs-12"
                    style={{ width: "8%", minWidth: "60px" }}
                  >
                    Out
                  </th>
                  <th
                    className="border border-secondary fs-12"
                    style={{ width: "8%", minWidth: "60px" }}
                  >
                    Up
                  </th>
                  <th
                    className="border border-secondary fs-12"
                    style={{ width: "8%", minWidth: "60px" }}
                  >
                    Down
                  </th>
                </>
              ) : (
                <>
                  <th
                    className="border border-secondary fs-12"
                    style={{ width: "11%", minWidth: "70px" }}
                  >
                    Backcurve
                  </th>
                  <th
                    className="border border-secondary fs-12"
                    style={{ width: "11%", minWidth: "70px" }}
                  >
                    Diameter
                  </th>
                  <th
                    className="border border-secondary fs-12"
                    style={{ width: "10%", minWidth: "80px" }}
                  >
                    Duration
                  </th>
                  <th
                    className="border border-secondary fs-12"
                    style={{ width: "15%", minWidth: "120px" }}
                  >
                    Duration Type
                  </th>
                </>
              )}

              <th
                className="border border-secondary fs-12"
                style={{ width: "12.5%", minWidth: "80px" }}
              >
                Brand
              </th>
              <th
                className="border border-secondary fs-12"
                style={{ width: "12.5%", minWidth: "80px" }}
              >
                Color
              </th>
            </tr>
          </thead>
          <tbody>
            {["distance", "near"].map((noteType) => (
              <tr key={noteType}>
                <td className="border border-secondary fs-12">
                  {noteType.charAt(0).toUpperCase() + noteType.slice(1)}
                </td>
                <td className="border border-secondary fs-12">
                  {renderField(
                    `${side}_${isLens ? "sph" : "power"}_${noteType}`,
                    true
                  )}
                </td>
                <td className="border border-secondary fs-12">
                  {renderField(`${side}_cyl_${noteType}`, true)}
                </td>
                <td className="border border-secondary fs-12">
                  {renderField(`${side}_axis_${noteType}`, true)}
                </td>

                {isLens ? (
                  <>
                    <td className="border border-secondary fs-12">
                      {renderField(`${side}_in_${noteType}`, true)}
                    </td>
                    <td className="border border-secondary fs-12">
                      {renderField(`${side}_out_${noteType}`, true)}
                    </td>
                    <td className="border border-secondary fs-12">
                      {renderField(`${side}_up_${noteType}`, true)}
                    </td>
                    <td className="border border-secondary fs-12">
                      {renderField(`${side}_down_${noteType}`, true)}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="border border-secondary fs-12">
                      {renderField(`${side}_backcurve_${noteType}`, true)}
                    </td>
                    <td className="border border-secondary fs-12">
                      {renderField(`${side}_diameter_${noteType}`, true)}
                    </td>
                    <td className="border border-secondary fs-12">
                      {isEditable
                        ? renderDuration(`${side}_duration`)
                        : stepFormData?.[`${side}_duration`] || ""}
                    </td>

                    <td className="border border-secondary fs-12">
                      {isEditable
                        ? renderDurationUnit(`${side}_duration_unit`)
                        : stepFormData?.[`${side}_duration_unit`] || ""}
                    </td>
                  </>
                )}

                <td className="border border-secondary fs-12">
                  {renderField(`${side}_brand`, false)}
                </td>
                <td className="border border-secondary fs-12">
                  {renderField(`${side}_color`, false)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      );
    }, [stepFormData, isLens, renderSelect, renderTextInput, renderDuration]
  );


  return (
    <>
      <Card className="border border-2 border-warning mb-3 shadow-sm">
        <Card.Header className="text-warning badge-subtle-warning border-bottom border-warning d-flex justify-content-between align-items-center">
          <div className="d-flex fs-12 fw-semibold align-items-center">
            <Eye className="me-2" />
            <span>Optical Details</span>
          </div>
          {isDisabled && Number(claimsSetting?.submit) === 1 && (
            <PencilSquare
              role="button"
              size={12}
              className="text-warning"
              onClick={() => {
                reset(stepFormData);
                setShowModal(true);
              }}
            />
          )}
        </Card.Header>
        <Card.Body className="p-2">
          <Row className="g-2">
            {opticalData.map((item) => (
              <Col key={item.id} xs={12} md={4} xl={3} className="mb-2">
                <div className="d-flex flex-column gap-1 w-100">
                  <div className="fs-12 text-800 fw-bold">{item?.title}</div>
                  <div
                    className={`${"text-600"} fw-normal text-capitalize fs-12`}
                  >
                    {item?.value}
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {(isLens || isContact) && (
            <motion.div
              style={{ border: "2px solid #2ca271" }}
              className="rounded p-3 position-relative mt-4"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.5 }}
            >
              <div className="position-absolute top-0 start-50 translate-middle px-2">
                <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success fs-12">
                  <span>{productType || "-"}</span>
                </span>
              </div>
              <Row className="g-2 mt-3">
                <Col xs={12}>{renderEyeTable("Right")}</Col>
                <Col xs={12}>{renderEyeTable("Left")}</Col>
              </Row>
            </motion.div>
          )}
        </Card.Body>
      </Card>

      <OpticalDetailsEditModal
        showModal={showModal}
        setShowModal={setShowModal}
        productType={productType}
        isLens={isLens}
        isContact={isContact}
        control={control}
        watch={watch}
        reset={reset}
        setValue={setValue}
        handleSubmit={handleSubmit}
        errors={errors}
        renderEyeTable={renderEyeTable}
        onSubmit={onSubmit}
        renderSelect={renderSelect}
        renderTextInput={renderTextInput}
        renderDuration={renderDuration}
      />
    </>
  );
};

export default OpticalDetailsRequest;
